import type { Socket } from "socket.io-client"
import {
  subscribeToPrices,
  addPriceSubscription,
  removePriceSubscription,
  unsubscribeFromPrices,
} from "./intraday-prices"

interface AccountSubscriptionState {
  idsByConsumer: Map<string, string[]>
  refCounts: Map<string, number>
  subscribedIds: Set<string>
  modelId: string | null
  socket: Socket | null
  connectHandler: (() => void) | null
}

const stateByAccountId = new Map<string, AccountSubscriptionState>()

function getOrCreateState(accountId: string): AccountSubscriptionState {
  let state = stateByAccountId.get(accountId)
  if (!state) {
    state = {
      idsByConsumer: new Map(),
      refCounts: new Map(),
      subscribedIds: new Set(),
      modelId: null,
      socket: null,
      connectHandler: null,
    }
    stateByAccountId.set(accountId, state)
  }
  return state
}

function detachConnectHandler(state: AccountSubscriptionState): void {
  if (state.socket && state.connectHandler) {
    state.socket.off("connect", state.connectHandler)
  }
  state.socket = null
  state.connectHandler = null
}

function ensureConnectHandler(socket: Socket, accountId: string, state: AccountSubscriptionState): void {
  if (state.socket === socket && state.connectHandler) return
  detachConnectHandler(state)
  const connectHandler = (): void => resyncPriceSubscriptions(socket, accountId)
  socket.on("connect", connectHandler)
  state.socket = socket
  state.connectHandler = connectHandler
}

function applyConsumerIds(
  state: AccountSubscriptionState,
  previousIds: string[],
  nextIds: string[],
): { added: string[], removed: string[] } {
  const added: string[] = []
  const removed: string[] = []
  const previousSet = new Set(previousIds)
  const nextSet = new Set(nextIds)

  for (const modelProviderId of previousSet) {
    if (nextSet.has(modelProviderId)) continue
    const nextCount = (state.refCounts.get(modelProviderId) ?? 0) - 1
    if (nextCount <= 0) {
      state.refCounts.delete(modelProviderId)
      removed.push(modelProviderId)
    } else {
      state.refCounts.set(modelProviderId, nextCount)
    }
  }

  for (const modelProviderId of nextSet) {
    if (previousSet.has(modelProviderId)) continue
    const previousCount = state.refCounts.get(modelProviderId) ?? 0
    state.refCounts.set(modelProviderId, previousCount + 1)
    if (previousCount === 0) added.push(modelProviderId)
  }

  return { added, removed }
}

function syncSocket(
  socket: Socket,
  state: AccountSubscriptionState,
  added: string[],
  removed: string[],
  modelId: string,
  forceFullResubscribe: boolean,
): void {
  for (const modelProviderId of removed) state.subscribedIds.delete(modelProviderId)
  for (const modelProviderId of added) state.subscribedIds.add(modelProviderId)

  if (!socket.connected) return

  // Full-resubscribe the whole union (a superset, so clobber-free) to force the snapshot an add can't.
  if (forceFullResubscribe && state.subscribedIds.size > 0) {
    subscribeToPrices(socket, [...state.subscribedIds], modelId)
    return
  }

  if (added.length > 0) addPriceSubscription(socket, added, modelId)

  if (removed.length > 0) {
    if (state.subscribedIds.size === 0) {
      unsubscribeFromPrices(socket)
    } else {
      removePriceSubscription(socket, removed)
    }
  }
}

export function setPriceSubscription(
  socket: Socket,
  accountId: string,
  consumerId: string,
  modelProviderIds: string[],
  modelId: string,
): void {
  const state = getOrCreateState(accountId)
  state.modelId = modelId
  ensureConnectHandler(socket, accountId, state)
  const previousIds = state.idsByConsumer.get(consumerId) ?? []
  const previousIdSet = new Set(previousIds)
  const consumerGainedIds = modelProviderIds.some((modelProviderId) => !previousIdSet.has(modelProviderId))
  const { added, removed } = applyConsumerIds(state, previousIds, modelProviderIds)
  state.idsByConsumer.set(consumerId, [...modelProviderIds])
  // Gained ids already in the union: an incremental add would no-op server-side, skipping the snapshot.
  const gainedOnlyAlreadySubscribedIds = consumerGainedIds && added.length === 0
  syncSocket(socket, state, added, removed, modelId, gainedOnlyAlreadySubscribedIds)
}

export function removePriceSubscriptionConsumer(
  socket: Socket,
  accountId: string,
  consumerId: string,
): void {
  const state = stateByAccountId.get(accountId)
  if (!state) return
  const previousIds = state.idsByConsumer.get(consumerId)
  if (!previousIds) return
  const { added, removed } = applyConsumerIds(state, previousIds, [])
  state.idsByConsumer.delete(consumerId)
  syncSocket(socket, state, added, removed, state.modelId ?? "", false)
  if (state.idsByConsumer.size === 0) {
    detachConnectHandler(state)
    stateByAccountId.delete(accountId)
  }
}

export function resyncPriceSubscriptions(socket: Socket, accountId: string): void {
  const state = stateByAccountId.get(accountId)
  if (!state?.modelId) return
  const union = [...state.subscribedIds]
  if (union.length > 0) subscribeToPrices(socket, union, state.modelId)
}

export function _getSubscribedPriceIdsForTesting(accountId: string): string[] {
  return [...(stateByAccountId.get(accountId)?.subscribedIds ?? [])]
}

export function _resetPriceSubscriptionsForTesting(): void {
  stateByAccountId.clear()
}
